import { useEffect, useMemo, useState } from "react";
import { Timer } from "lucide-react";
import { useGame } from "./useGame";
import { GameShell, cardCls } from "./GameShell";

const Board = ({ g, onResult }) => {
  const timed = g.kind === "timeline";
  const pool = useMemo(() => [...g.steps].sort(() => Math.random() - 0.5), [g]);
  const [placed, setPlaced] = useState([]);
  const [wrong, setWrong] = useState(null);
  const [missed, setMissed] = useState([]);
  const [start] = useState(() => Date.now());
  const [secs, setSecs] = useState(0);
  const finished = placed.length === g.steps.length;
  useEffect(() => { if (!timed || finished) return; const t = setInterval(() => setSecs(Math.round((Date.now() - start) / 1000)), 500); return () => clearInterval(t); }, [timed, finished, start]);
  const byId = Object.fromEntries(g.steps.map((s) => [s.id, s]));
  const place = (id) => {
    if (finished || !byId[id] || placed.includes(id)) return;
    const want = g.steps[placed.length].id;
    if (id !== want) { setMissed((m) => (m.includes(want) ? m : [...m, want])); setWrong(id); setTimeout(() => setWrong(null), 450); return; }
    const next = [...placed, id]; setPlaced(next);
    if (next.length === g.steps.length) onResult({ kind: g.kind, title: g.title, correct: g.steps.length - missed.length, total: g.steps.length, ...(timed ? { seconds: Math.round((Date.now() - start) / 1000) } : {}) });
  };
  return (
    <div data-testid={`${g.kind}-game`}>
      <div className="flex items-center gap-3"><p className="font-display text-xl text-[#2C4A3E] flex-1" data-testid="game-title">{g.title}</p>
        {timed && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#2C4A3E] text-white text-sm tabular-nums" data-testid="timeline-timer"><Timer className="w-4 h-4" />{secs}s</span>}</div>
      <p className="text-xs text-stone-500 mt-1">Tap (or drag) the cards in the right order, starting with what comes first.</p>
      <ol onDragOver={(e) => e.preventDefault()} onDrop={(e) => place(e.dataTransfer.getData("text/plain"))} className={`mt-4 ${timed ? "relative border-l-2 border-[#2C4A3E]/30 ml-3 pl-6" : "grid sm:grid-cols-2 gap-3"} space-y-0`} data-testid="sequence-slots">
        {g.steps.map((_, i) => {
          const s = byId[placed[i]];
          return (
            <li key={i} className={`${timed ? "relative mb-3" : ""} rounded-2xl border-2 border-dashed ${s ? "border-[#6B9080] bg-[#EEF3EF]" : i === placed.length ? "border-[#C87D53] bg-[#FBF1EA]" : "border-[#E6DEC3] bg-white/40"} p-3 min-h-[56px] flex items-center gap-3`} data-testid={`sequence-slot-${i}`}>
              {timed && <span className="absolute -left-[33px] w-4 h-4 rounded-full bg-[#2C4A3E] ring-4 ring-[#FDFBF7]" />}
              <span className="w-7 h-7 rounded-full bg-[#2C4A3E] text-white text-sm grid place-items-center shrink-0">{i + 1}</span>
              {s ? <span className="text-sm text-[#2C4A3E]">{s.label && <b className="mr-2 text-[#C87D53]">{s.label}</b>}{s.text}</span> : <span className="text-xs text-stone-400">{i === placed.length ? "Next…" : ""}</span>}
            </li>);
        })}
      </ol>
      <div className="mt-4 flex flex-wrap gap-2" data-testid="sequence-pool">
        {pool.filter((s) => !placed.includes(s.id)).map((s) => (
          <button key={s.id} type="button" draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", s.id)} onClick={() => place(s.id)} data-testid={`sequence-card-${s.id}`}
            className={`${cardCls(false, wrong === s.id)} px-4 py-3 text-sm text-left max-w-full`}>{s.text}</button>))}
      </div>
    </div>
  );
};

export const SequenceGame = ({ req, lesson, onResult, kind }) => {
  const { g, err, load } = useGame(kind, req, lesson);
  return <GameShell g={g} err={err} load={load}>{g && <Board g={g} onResult={onResult} />}</GameShell>;
};
